import { Box, Stack } from '@mui/material';
import { CardOnWheelsIcon, ConeIcon, ReceiptIcon } from '@vayufp/coast-components';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { spendIntentValidationSchemaV2 } from '../../../../config/page-validation-schema';
import { FormikCheckboxCard } from '../../../../general/inputs/formik-checkbox-card';
import { FormikCurrencyField } from '../../../../general/inputs/formik-currency-field';
import { RootState } from '../../../../store';
import { SubmitWorkflowWithBack } from '../../../workflow/components/submit-workflow-with-back';
import {
  SpendIntentPayload,
  saveSpendIntent,
  spendIntentKeysV2,
  monthlyNonFuelSpendIntentKeys,
} from '../../reducers/business-info';

const selector = ({
  businessInfo: {
    info: {
      fleetAndMaintenance,
      travelAndRestaurants,
      materialsAndSupplies,
      generalBusiness,
      monthlyNonFuelSpend,
      defAndVehicleSupplies,
      iceWaterFood,
    },
  },
  nextStep: { loading: appLoading },
}: RootState) => ({
  spendIntent: {
    fleetAndMaintenance,
    travelAndRestaurants,
    materialsAndSupplies,
    generalBusiness,
    monthlyNonFuelSpend,
    defAndVehicleSupplies,
    iceWaterFood,
  },
  saving: appLoading,
});

const calculateMonthlyNonFuelSpend = (values: SpendIntentPayload): SpendIntentPayload['monthlyNonFuelSpend'] => {
  // If all spend intent checkboxes are false, return null
  const shouldBeNull = Object.keys(values)
    .filter((key) => (spendIntentKeysV2 as string[]).includes(key) && key !== 'monthlyNonFuelSpend')
    .every((key) => values[key as keyof SpendIntentPayload] === false);

  return shouldBeNull ? null : values.monthlyNonFuelSpend;
};

const SpendIntentForm = () => {
  const dispatch = useDispatch();
  const { spendIntent, saving } = useSelector(selector);
  const [showNextBtn, setShowNextBtn] = useState(false);
  const [showMonthlyNonFuelSpend, setShowMonthlyNonFuelSpend] = useState(false);
  const formik = useFormik({
    initialValues: {
      ...spendIntent,
      fleetAndMaintenance: spendIntent.fleetAndMaintenance || false,
      travelAndRestaurants: spendIntent.travelAndRestaurants || false,
      materialsAndSupplies: spendIntent.materialsAndSupplies || false,
      generalBusiness: null,
      defAndVehicleSupplies: null,
      iceWaterFood: null,
      monthlyNonFuelSpend: spendIntent.monthlyNonFuelSpend || null,
    },
    validationSchema: spendIntentValidationSchemaV2,
    validateOnChange: true,
    onSubmit: (values) => {
      const payload: SpendIntentPayload = {
        ...values,
        monthlyNonFuelSpend: calculateMonthlyNonFuelSpend(values),
      };
      dispatch(saveSpendIntent(payload));
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    const checkboxWasChecked = Object.entries(formik.values).some(
      ([key, value]) => (spendIntentKeysV2 as string[]).includes(key) && value === true,
    );
    const monthlyNonFuelWasChecked = Object.entries(formik.values).some(
      ([key, value]) => (monthlyNonFuelSpendIntentKeys as string[]).includes(key) && value === true,
    );
    if (checkboxWasChecked && monthlyNonFuelWasChecked) {
      setShowNextBtn(true);
      setShowMonthlyNonFuelSpend(true);
    } else if (checkboxWasChecked) {
      setShowNextBtn(true);
      setShowMonthlyNonFuelSpend(false);
    } else {
      setShowNextBtn(false);
      setShowMonthlyNonFuelSpend(false);
    }
  }, [formik.values]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box my={1.5}>
        <Stack spacing={1}>
          <FormikCheckboxCard
            label="Fleet and Maintenance"
            description="Vehicle-related expenses such as maintenance, parts, oil changes, or parking"
            icon={
              <Box sx={{ width: 45, height: 45 }}>
                <ConeIcon />
              </Box>
            }
            id="fleetAndMaintenance"
            name="fleetAndMaintenance"
            formik={formik}
          />
          <FormikCheckboxCard
            label="Travel and Restaurants"
            description="Travel expenses for employees or drivers traveling for work, including hotels, transportation, or restaurants"
            icon={
              <Box sx={{ width: 45, height: 45 }}>
                <CardOnWheelsIcon />
              </Box>
            }
            id="travelAndRestaurants"
            name="travelAndRestaurants"
            formik={formik}
          />
          <FormikCheckboxCard
            label="Materials and Supplies"
            description="Expenses related to what's needed on the job or bought centrally at hardware stores, supply houses, or wholesale clubs"
            icon={
              <Box sx={{ width: 45, height: 45 }}>
                <ReceiptIcon />
              </Box>
            }
            id="materialsAndSupplies"
            name="materialsAndSupplies"
            formik={formik}
          />
        </Stack>
      </Box>
      {showMonthlyNonFuelSpend && (
        <Box display="flex" justifyContent="center" mt={1}>
          <FormikCurrencyField
            formik={formik}
            id="monthlyNonFuelSpend"
            name="monthlyNonFuelSpend"
            label="Expected monthly spend for non-fuel categories"
            inputProps={{
              inputMode: 'numeric',
            }}
            sx={{ maxWidth: 350 }}
            placeholder="Optional"
            data-public
          />
        </Box>
      )}
      <SubmitWorkflowWithBack type="submit" progress={saving} sx={{ mt: 3 }}>
        {showNextBtn ? 'Next' : 'Skip'}
      </SubmitWorkflowWithBack>
    </form>
  );
};

export { SpendIntentForm };
